import { v4 } from 'uuid'
import wait from '@ancon/wildcat-utils/shared/wait'
import getFullName from '@ancon/wildcat-utils/user/getFullName'

import api from '../../../api'
import createAppAsyncThunk from '../../../store/createAppAsyncThunk'
import serializeError from '../../../store/utils/serializeError'
import getLocaleTag from '../../app/utils/getLocalTag'
import {
  clientContextCurrentCompanyUserIdSelector,
  clientContextCurrentUserCompanyIdSelector,
  clientContextCustomerDetailsSelector,
  clientContextCustomerIdSelector,
  clientContextCustomerSelector,
} from '../../clientContext/store/clientContextSelectors'
import {
  BookingWriteKind,
  GuestAvailability,
  GuestFormFieldMode,
  GuestReservationResponse,
  GuestWaitlistResponse,
  GuestWidget,
  ReservationStatus,
  TableFinderParams,
  TableFinderResult,
  VisitListItem,
} from '../types'
import {
  ConfirmationPoll,
  FinderResultLimit,
  FinderTimeChipCount,
  MyVisitsLimit,
} from '../constants'
import getFinderSearchWindow from '../utils/getFinderSearchWindow'
import getTableFinderResult from '../utils/getTableFinderResult'
import zonedDateTimeToUtc from '../utils/zonedDateTimeToUtc'
import isUpcomingReservation from '../utils/isUpcomingReservation'
import type { RootState } from '../../../store/types'

import { bookingSetIdempotencyKey } from './bookingSlice'

/** One key per kind of write, reused when the same write is retried */
function getIdempotencyKey(
  kind: BookingWriteKind,
  getState: () => RootState,
  dispatch: (action: ReturnType<typeof bookingSetIdempotencyKey>) => void,
) {
  const existing = getState().booking.idempotencyKeys[kind]

  if (existing) {
    return existing
  }

  const key = v4()
  dispatch(bookingSetIdempotencyKey({ kind, key }))

  return key
}

export const searchTableFinder = createAppAsyncThunk<
  TableFinderResult[],
  TableFinderParams
>('booking/searchTableFinder', async (params, { rejectWithValue }) => {
  try {
    const { from, until, preferredTime } = getFinderSearchWindow(params.time)

    const { data: restaurants } = await api.reservation.restaurants.get.list({
      city: params.city,
      businessDate: params.date,
      from,
      until,
      partySize: params.guests,
      limit: FinderResultLimit,
      preferredTime,
      timeCount: FinderTimeChipCount,
    })

    return restaurants.map(restaurant =>
      getTableFinderResult(restaurant, params),
    )
  } catch (error) {
    return rejectWithValue(serializeError(error))
  }
})

export const fetchBookingWidget = createAppAsyncThunk<
  GuestWidget,
  { widgetKey: string }
>('booking/fetchBookingWidget', async ({ widgetKey }, { rejectWithValue }) => {
  try {
    const { data } = await api.reservation.widgets.get.byKey({ widgetKey })

    return data
  } catch (error) {
    return rejectWithValue(serializeError(error))
  }
})

export const fetchBookingAvailability = createAppAsyncThunk<GuestAvailability>(
  'booking/fetchBookingAvailability',
  async (_, { getState, rejectWithValue }) => {
    const { widget, bookingTypeId, businessDate, partySize } =
      getState().booking

    try {
      const { data } = await api.reservation.widgets.get.availability({
        widgetKey: widget!.widgetKey,
        bookingTypeId: bookingTypeId!,
        businessDate: businessDate!,
        partySize,
      })

      return data
    } catch (error) {
      return rejectWithValue(serializeError(error))
    }
  },
)

export const createBookingHold = createAppAsyncThunk<GuestReservationResponse>(
  'booking/createBookingHold',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const {
      widget,
      bookingTypeId,
      businessDate,
      partySize,
      selectedStartsAt,
      packageId,
    } = getState().booking

    const idempotencyKey = getIdempotencyKey(
      BookingWriteKind.Hold,
      getState,
      dispatch,
    )

    try {
      const { data } = await api.reservation.reservations.post.hold(
        {
          idempotencyKey,
          booking: {
            bookingTypeId: bookingTypeId!,
            businessDate: businessDate!,
            startsAt: selectedStartsAt!,
            partySize,
            packageId,
          },
        },
        { tenantId: widget!.tenantId, outletId: widget!.outletId },
        { 'X-Widget-Key': widget!.widgetKey },
      )

      return data
    } catch (error) {
      return rejectWithValue(serializeError(error))
    }
  },
)

export const submitBookingDetails = createAppAsyncThunk<
  GuestReservationResponse,
  { locale: string }
>(
  'booking/submitBookingDetails',
  async ({ locale }, { getState, dispatch, rejectWithValue }) => {
    const state = getState()
    const {
      widget,
      reservation,
      specialRequest,
      upsellSelections,
      isCompanyBooking,
      packageId,
    } = state.booking

    const customer = clientContextCustomerSelector(state)
    const customerDetails = clientContextCustomerDetailsSelector(state)
    const companyId = clientContextCurrentUserCompanyIdSelector(state)
    const memberId = clientContextCurrentCompanyUserIdSelector(state)

    const idempotencyKey = getIdempotencyKey(
      BookingWriteKind.Details,
      getState,
      dispatch,
    )

    try {
      const { data } = await api.reservation.reservations.put.guestDetails(
        {
          idempotencyKey,
          guestName: getFullName(customer, ''),
          // An outlet with the email field off rejects any email
          guestEmail:
            widget!.formFields?.email === GuestFormFieldMode.Off
              ? null
              : customerDetails?.email || null,
          guestPhone: customer?.telephoneNumber || null,
          guestLanguageCode: getLocaleTag(locale),
          specialRequest: specialRequest.trim() || null,
          // Extras replace the hold's selection, so the package goes along
          extras: {
            packageId,
            upsells: upsellSelections.filter(({ quantity }) => quantity > 0),
          },
          // The verified company path: the signed-in company session, never a lookup
          company:
            isCompanyBooking && companyId && memberId
              ? { companyId, memberId }
              : null,
        },
        {
          tenantId: widget!.tenantId,
          outletId: widget!.outletId,
          reservationId: reservation!.reservation.id,
        },
        { 'X-Widget-Key': widget!.widgetKey },
      )

      return data
    } catch (error) {
      return rejectWithValue(serializeError(error))
    }
  },
)

export const confirmBooking = createAppAsyncThunk<GuestReservationResponse>(
  'booking/confirmBooking',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const state = getState()
    const { widget, reservation } = state.booking
    const params = {
      tenantId: widget!.tenantId,
      outletId: widget!.outletId,
      reservationId: reservation!.reservation.id,
    }

    const idempotencyKey = getIdempotencyKey(
      BookingWriteKind.Confirm,
      getState,
      dispatch,
    )

    try {
      let { data } = await api.reservation.reservations.post.confirm(
        { idempotencyKey },
        params,
        { 'X-Widget-Key': widget!.widgetKey },
      )

      // Core creates the check a moment after Confirm; read until it settles
      for (
        let attempt = 0;
        attempt < ConfirmationPoll.Attempts &&
        data.reservation.status === ReservationStatus.Pending &&
        !data.check;
        attempt += 1
      ) {
        await wait(ConfirmationPoll.IntervalMs)

        try {
          const response =
            await api.reservation.reservations.get.details(params)
          data = response.data
        } catch (error) {
          // Not settled yet: keep the last answer
        }
      }

      return data
    } catch (error) {
      return rejectWithValue(serializeError(error))
    }
  },
)

export const joinBookingWaitlist = createAppAsyncThunk<
  GuestWaitlistResponse,
  { guestName: string; guestPhone: string; locale: string }
>(
  'booking/joinBookingWaitlist',
  async (
    { guestName, guestPhone, locale },
    { getState, dispatch, rejectWithValue },
  ) => {
    const state = getState()
    const { widget, bookingTypeId, businessDate, partySize } = state.booking
    const customerDetails = clientContextCustomerDetailsSelector(state)

    const idempotencyKey = getIdempotencyKey(
      BookingWriteKind.Waitlist,
      getState,
      dispatch,
    )

    try {
      const { data } = await api.reservation.waitlistEntries.post.join(
        {
          idempotencyKey,
          bookingTypeId: bookingTypeId!,
          businessDate: businessDate!,
          // Any time that day
          preferredStartsAt: zonedDateTimeToUtc(
            businessDate!,
            '00:00',
            widget!.timeZoneId,
          ),
          preferredEndsAt: zonedDateTimeToUtc(
            businessDate!,
            '23:59',
            widget!.timeZoneId,
          ),
          partySize,
          guestName,
          guestEmail: customerDetails?.email || null,
          guestPhone,
          guestLanguageCode: getLocaleTag(locale),
        },
        { tenantId: widget!.tenantId, outletId: widget!.outletId },
        { 'X-Widget-Key': widget!.widgetKey },
      )

      return data
    } catch (error) {
      return rejectWithValue(serializeError(error))
    }
  },
)

/**
 * The signed-in guest's bookings across restaurants, newest first. The
 * service finds them from the Firebase token, on any device.
 */
export const fetchMyVisits = createAppAsyncThunk<VisitListItem[]>(
  'booking/fetchMyVisits',
  async (_, { getState, rejectWithValue }) => {
    const customerId = clientContextCustomerIdSelector(getState())

    if (!customerId) {
      return []
    }

    try {
      const { data } = await api.reservation.reservations.get.list({
        'Extent.Outlets': true,
        offset: 0,
        limit: MyVisitsLimit,
      })

      const now = Date.now()

      return data.reservations.map(reservation => ({
        reservation,
        outlet:
          data.outlets?.find(outlet => outlet.id === reservation.outletId) ??
          null,
        isUpcoming: isUpcomingReservation(reservation, now),
      }))
    } catch (error) {
      return rejectWithValue(serializeError(error))
    }
  },
)
